import type { VehicleRow, SpecRow } from "@/lib/vehicle-queries";
import {
  article,
  cleanEngineDesc,
  cleanTransmission,
  isPhev,
  mpgUnit,
  sameFamily,
} from "@/lib/vehicle-text";

export interface BlufInput {
  makeName: string;
  modelName: string;
  vehicles: VehicleRow[];
  specs?: SpecRow[];
}

function bestMpgVehicle(vehicles: VehicleRow[]): VehicleRow | null {
  return vehicles.reduce<VehicleRow | null>(
    (best, v) =>
      v.mpg_combined && (!best || (best.mpg_combined ?? 0) < v.mpg_combined) ? v : best,
    null,
  );
}

function bestTowing(specs: SpecRow[]): SpecRow | null {
  return specs.reduce<SpecRow | null>(
    (best, s) =>
      s.towing_capacity_lbs && (!best || (best.towing_capacity_lbs ?? 0) < s.towing_capacity_lbs)
        ? s
        : best,
    null,
  );
}

/**
 * EPA trim strings are verbose ("Auto (AV-S6), 4 cyl, 2.5 L"). Keep only the
 * leading human-readable trim label, and drop it entirely when it is noise.
 */
function cleanTrim(raw: string | null): string {
  if (!raw) return "";
  const first = (raw.split(",")[0] ?? "").trim();
  if (!first || first.length > 20) return "";
  if (/^(auto|manual|awd|fwd|rwd|4wd|2wd|\d)/i.test(first)) return "";
  return first;
}

/** Alternative metric sentence when MPG or towing data is missing. */
function altMetric(v: VehicleRow | null, bestRange: VehicleRow | null): string | null {
  const rangeRow = bestRange?.ev_range_miles ? bestRange : v?.ev_range_miles ? v : null;
  const range = rangeRow?.ev_range_miles ?? null;
  if (range)
    return isPhev(rangeRow)
      ? `a plug-in hybrid total driving range of up to ${range} miles`
      : `an EPA-rated electric driving range of up to ${range} miles`;
  if (!v) return null;
  // engine_desc is often an EPA acronym ("SIDI", "PFI"); prefer real displacement.
  const desc = cleanEngineDesc(v.engine_desc);
  if (desc) {
    const label = desc.toLowerCase();
    if (v.displacement) return `a ${v.displacement}L ${label} powertrain`;
    return `${article(label)} ${label} powertrain`;
  }
  if (v.displacement && v.cylinders) return `a ${v.displacement}L ${v.cylinders}-cylinder engine`;
  if (v.displacement) return `a ${v.displacement}L engine`;
  if (v.cylinders) return `a ${v.cylinders}-cylinder engine`;
  if (cleanTransmission(v.transmission))
    return `${article(cleanTransmission(v.transmission))} ${cleanTransmission(
      v.transmission,
    )} transmission`;
  if (v.drive) return `${v.drive} drive`;
  if (v.vehicle_class) return `${v.vehicle_class} body-style engineering`;
  return null;
}

/**
 * Opening verb, selected from the vehicle's own powertrain and body class so
 * two different models do not read as the same sentence with a name swapped.
 */
function opener(ref: VehicleRow | null, isEv: boolean, towing: number | null): string {
  if (isEv) return "is rated by the EPA at";
  if (towing && towing >= 7000) return "is certified for";
  const cls = (ref?.vehicle_class ?? "").toLowerCase();
  if (/truck|van|cab/.test(cls)) return "carries";
  if (/suv|sport utility|wagon/.test(cls)) return "returns";
  if (/two seater|subcompact|compact/.test(cls)) return "posts";
  return "features";
}

/**
 * A second, model-specific sentence built only from figures that vary between
 * models: the spread across model years, the size of the lineup, and the
 * drivetrain mix actually recorded for this vehicle.
 */
function spreadSentence(vehicles: VehicleRow[], name: string): string {
  if (vehicles.length === 0) return "";
  const years = vehicles.map((v) => v.year).filter((y): y is number => typeof y === "number");
  const combined = vehicles
    .map((v) => v.mpg_combined)
    .filter((m): m is number => typeof m === "number");
  const drives = Array.from(new Set(vehicles.map((v) => v.drive).filter(Boolean)));
  const engines = Array.from(
    new Set(
      vehicles
        .map((v) => (v.displacement == null ? null : Number(v.displacement)))
        .filter((d): d is number => typeof d === "number" && Number.isFinite(d)),
    ),
  ).sort((a, b) => a - b);
  const parts: string[] = [];
  if (combined.length > 1) {
    const lo = Math.min(...combined);
    const hi = Math.max(...combined);
    if (hi > lo) {
      parts.push(
        `combined fuel economy runs from ${lo} to ${hi} MPG, a ${Math.round(
          ((hi - lo) / lo) * 100,
        )}% spread between the thirstiest and most efficient configuration`,
      );
    } else {
      parts.push(`every recorded configuration is rated at the same ${hi} MPG combined`);
    }
  }
  if (engines.length > 1) {
    parts.push(
      `${engines.length} engine displacements from ${engines[0]}L to ${engines[engines.length - 1]}L`,
    );
  } else if (engines.length === 1) {
    parts.push(`a single ${engines[0]}L displacement across the range`);
  }
  if (drives.length > 1) parts.push(`${drives.length} drivetrain layouts (${drives.join(", ")})`);
  else if (drives.length === 1) parts.push(`${drives[0]} only`);
  if (parts.length === 0) return "";
  const span =
    years.length && Math.min(...years) !== Math.max(...years)
      ? `Across ${Math.min(...years)}–${Math.max(...years)}, the ${name} records ${vehicles.length} configurations: `
      : `The dataset holds ${vehicles.length} configuration${vehicles.length === 1 ? "" : "s"} for this vehicle: `;
  return ` ${span}${parts.join("; ")}.`;
}

/**
 * Bottom-Line-Up-Front atomic passage. Rendered server-side so AI crawlers
 * extract the fully parsed answer text from raw HTML.
 */
export function buildBlufSummary(input: BlufInput): string | null {
  const { makeName, modelName, vehicles, specs = [] } = input;
  if (vehicles.length === 0 && specs.length === 0) return null;

  const towSpec = bestTowing(specs);
  // Entity anchor: always the newest model year in the dataset, so the BLUF,
  // the H1 and the FAQ headings all name the same vehicle.
  const allYears = [...vehicles.map((v) => v.year), ...specs.map((s) => s.year)].filter(
    (y): y is number => typeof y === "number",
  );
  const latestYear = allYears.length ? Math.max(...allYears) : null;
  const latestVehicle = latestYear ? (vehicles.find((v) => v.year === latestYear) ?? null) : null;
  // Peak figures must come from the same powertrain family as the current car.
  const family = sameFamily(vehicles, latestVehicle ?? vehicles[0] ?? null);
  const mpgVehicle = bestMpgVehicle(family);
  const ref = latestVehicle ?? mpgVehicle ?? vehicles[0] ?? null;
  const trim = cleanTrim(latestVehicle?.trim ?? mpgVehicle?.trim ?? ref?.trim ?? null);
  const name = [latestYear, makeName, modelName, trim].filter(Boolean).join(" ");

  const mpg = mpgVehicle?.mpg_combined ?? null;
  const unit = mpgUnit(mpgVehicle);
  const bestRange = family.reduce<VehicleRow | null>(
    (b, v) => (v.ev_range_miles && (!b || (b.ev_range_miles ?? 0) < v.ev_range_miles) ? v : b),
    null,
  );
  const towing = towSpec?.towing_capacity_lbs ?? null;
  // Peak figures recorded on an older configuration are labelled as historical.
  const mpgHistoric = Boolean(latestYear && mpgVehicle?.year && mpgVehicle.year !== latestYear);
  const towHistoric = Boolean(latestYear && towSpec?.year && towSpec.year !== latestYear);
  const mpgPhrase = mpgHistoric
    ? `a historical peak fuel economy rating of up to ${mpg} ${unit} recorded in previous configurations (${mpgVehicle?.year})`
    : `an official EPA fuel economy rating of up to ${mpg} ${unit}`;
  const towPhrase = towing
    ? towHistoric
      ? `a historical peak towing capacity of ${towing.toLocaleString()} lbs recorded in previous configurations (${towSpec?.year})`
      : `a maximum verified towing capacity of ${towing.toLocaleString()} lbs when properly equipped`
    : "";

  const verb = opener(ref, Boolean(bestRange?.ev_range_miles), towing);
  const tail = spreadSentence(vehicles, `${makeName} ${modelName}`);

  if (mpg && towing) {
    return `The ${name} ${verb} ${mpgPhrase}, along with ${towPhrase}.${tail}`;
  }

  const alt = altMetric(ref ?? null, bestRange);

  if (mpg) {
    return `The ${name} ${verb} ${mpgPhrase}${
      alt ? `, paired with ${alt}` : ""
    }. Verified towing figures are not published for this configuration.${tail}`;
  }

  if (towing) {
    return `The ${name} reaches ${towPhrase}${
      alt ? `, using ${alt}` : ""
    }.${tail}`;
  }

  if (alt) {
    return `The ${name} is documented here with ${alt}, along with verified drivetrain and configuration specifications for every recorded model year.${tail}`;
  }

  return `The ${name} is documented here with verified specifications for every recorded model year.${tail}`;
}
